import { useState } from 'react';
import { useTranslation } from '@parentpath/i18n';
import { Users, ArrowLeft, Check, Mail, Loader2, LogOut, UserCheck, Unlink, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/features/auth';
import { supabase } from '@/lib/supabase';
import type { OnboardingData } from '../pages/OnboardingPage';

interface ExistingConnection {
  id: string;
  partnerName: string | null;
  status: string;
}

interface PartnerStepProps {
  invitePartner: boolean;
  partnerEmail?: string;
  onChange: (updates: Partial<OnboardingData>) => void;
  onNext: () => void;
  onBack: () => void;
  isSubmitting: boolean;
  error: string | null;
  existingConnection?: ExistingConnection | null;
}

export function PartnerStep({
  invitePartner,
  partnerEmail,
  onChange,
  onNext,
  onBack,
  isSubmitting,
  error,
  existingConnection,
}: PartnerStepProps) {
  const { signOut } = useAuth();
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  const { t } = useTranslation('onboarding');

  const handleDisconnect = async () => {
    if (!existingConnection) return;

    setIsDisconnecting(true);
    setDisconnectError(null);

    try {
      const { error: updateError } = await supabase
        .from('partner_connections')
        .update({ status: 'removed' })
        .eq('id', existingConnection.id);

      if (updateError) {
        throw updateError;
      }

      // Reset state and continue with normal flow
      setShowDisconnectConfirm(false);
      // Force refresh by reloading the page
      window.location.reload();
    } catch (err) {
      console.error('Error disconnecting partner:', err);
      setDisconnectError(t('partnerStep.disconnectError'));
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleInviteChange = (invite: boolean) => {
    onChange({
      invitePartner: invite,
      partnerEmail: invite ? partnerEmail : undefined,
    });
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ partnerEmail: e.target.value });
  };

  const canProceed = !invitePartner || (partnerEmail && partnerEmail.includes('@'));

  // If user has an existing active connection, show that instead
  if (existingConnection) {
    return (
      <div>
        {/* Back Button */}
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-white/60 hover:text-white mb-6"
          disabled={isSubmitting || isDisconnecting}
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.back')}
        </button>

        {/* Icon */}
        <div className="w-16 h-16 mx-auto mb-6 bg-green-500/20 rounded-full flex items-center justify-center">
          <UserCheck className="w-8 h-8 text-green-400" />
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-white mb-2 text-center">
          {t('partnerStep.connectedTitle')}
        </h1>

        {/* Description */}
        <p className="text-white/70 mb-6 text-center">
          {t('partnerStep.connectedDesc', { name: existingConnection.partnerName || t('messages.yourPartner') })}
        </p>

        {/* Connected Partner Card */}
        <div className="bg-green-500/10 border border-green-500/30 rounded-[10px] p-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
              <Users className="w-5 h-5 text-green-400" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-white">
                {existingConnection.partnerName || 'Partner'}
              </p>
              <p className="text-sm text-green-400">{t('partnerStep.connectedStatus')}</p>
            </div>
          </div>
        </div>

        {/* Disconnect Confirmation */}
        {showDisconnectConfirm ? (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-[10px] p-4 mb-6">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-white mb-1">{t('partnerStep.disconnectTitle')}</p>
                <p className="text-sm text-white/70">
                  {t('partnerStep.disconnectDesc')}
                </p>
              </div>
            </div>

            {disconnectError && (
              <p className="text-red-300 text-sm mb-3">{disconnectError}</p>
            )}

            <div className="flex gap-2">
              <button
                onClick={handleDisconnect}
                disabled={isDisconnecting}
                className="flex-1 py-2 bg-red-500/20 border border-red-500/30 rounded-[10px] text-red-300 font-medium hover:bg-red-500/30 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isDisconnecting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Unlink className="w-4 h-4" />
                    {t('partnerStep.disconnectButton')}
                  </>
                )}
              </button>
              <button
                onClick={() => setShowDisconnectConfirm(false)}
                disabled={isDisconnecting}
                className="flex-1 py-2 bg-white/10 border border-white/20 rounded-[10px] text-white font-medium hover:bg-white/20 transition-colors disabled:opacity-50"
              >
                {t('partnerStep.disconnectCancel')}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowDisconnectConfirm(true)}
            className="w-full py-2 text-white/50 hover:text-white/70 text-sm transition-colors flex items-center justify-center gap-2 mb-4"
          >
            <Unlink className="w-4 h-4" />
            {t('partnerStep.disconnectLink')}
          </button>
        )}

        {/* CTA */}
        <button
          onClick={onNext}
          disabled={isSubmitting || isDisconnecting}
          className="w-full py-3 bg-pp-orange rounded-[10px] text-white font-semibold hover:bg-pp-orange/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t('common.settingUp')}
            </>
          ) : (
            t('common.next')
          )}
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-white/60 hover:text-white mb-6"
        disabled={isSubmitting}
      >
        <ArrowLeft className="w-4 h-4" />
        {t('common.back')}
      </button>

      {/* Icon */}
      <div className="w-16 h-16 mx-auto mb-6 bg-pink-500/20 rounded-full flex items-center justify-center">
        <Users className="w-8 h-8 text-pink-400" />
      </div>

      {/* Title */}
      <h1 className="text-2xl font-bold text-white mb-2 text-center">
        {t('partnerStep.title')}
      </h1>

      {/* Description */}
      <p className="text-white/70 mb-6 text-center">
        {t('partnerStep.description')}
      </p>

      {/* Options */}
      <div className="space-y-3 mb-6">
        {/* Yes Option */}
        <button
          onClick={() => handleInviteChange(true)}
          disabled={isSubmitting}
          className={cn(
            'w-full flex items-center justify-between p-4 rounded-[10px] border-2 transition-all text-left',
            invitePartner
              ? 'border-pp-orange bg-pp-orange/20'
              : 'border-white/20 bg-white/5 hover:border-white/30'
          )}
        >
          <div>
            <p className={cn(
              'font-medium',
              invitePartner ? 'text-white' : 'text-white/90'
            )}>
              {t('partnerStep.yes')}
            </p>
            <p className="text-sm text-white/50 mt-0.5">
              {t('partnerStep.yesDesc')}
            </p>
          </div>
          {invitePartner && (
            <div className="w-6 h-6 bg-pp-orange rounded-full flex items-center justify-center flex-shrink-0">
              <Check className="w-4 h-4 text-white" />
            </div>
          )}
        </button>

        {/* No Option */}
        <button
          onClick={() => handleInviteChange(false)}
          disabled={isSubmitting}
          className={cn(
            'w-full flex items-center justify-between p-4 rounded-[10px] border-2 transition-all text-left',
            !invitePartner
              ? 'border-pp-orange bg-pp-orange/20'
              : 'border-white/20 bg-white/5 hover:border-white/30'
          )}
        >
          <div>
            <p className={cn(
              'font-medium',
              !invitePartner ? 'text-white' : 'text-white/90'
            )}>
              {t('partnerStep.later')}
            </p>
            <p className="text-sm text-white/50 mt-0.5">
              {t('partnerStep.laterDesc')}
            </p>
          </div>
          {!invitePartner && (
            <div className="w-6 h-6 bg-pp-orange rounded-full flex items-center justify-center flex-shrink-0">
              <Check className="w-4 h-4 text-white" />
            </div>
          )}
        </button>
      </div>

      {/* Email Input (if inviting) */}
      {invitePartner && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-white/80 mb-2">
            {t('partnerStep.emailLabel')}
          </label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40" />
            <input
              type="email"
              value={partnerEmail || ''}
              onChange={handleEmailChange}
              placeholder={t('partnerStep.emailPlaceholder')}
              disabled={isSubmitting}
              className="w-full pl-12 px-4 py-3 rounded-[10px] bg-white/10 border border-white/20
                         text-white placeholder-white/40 caret-white
                         focus:border-pp-orange focus:ring-2 focus:ring-pp-orange/20
                         outline-none transition-all disabled:opacity-50"
            />
          </div>
          <p className="text-xs text-white/50 mt-2">
            {t('partnerStep.emailHint')}
          </p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/30 p-4 rounded-[10px] mb-6">
          <p className="text-red-300 text-sm mb-3">{error}</p>
          <div className="flex gap-2">
            <button
              onClick={onNext}
              disabled={isSubmitting}
              className="px-3 py-1.5 bg-white/10 border border-white/20 rounded-[5px] text-white text-sm hover:bg-white/20 transition-colors"
            >
              {t('common.retry')}
            </button>
            <button
              onClick={() => signOut()}
              className="px-3 py-1.5 text-white/60 text-sm hover:text-white transition-colors flex items-center gap-1"
            >
              <LogOut className="w-4 h-4" />
              {t('common.logout')}
            </button>
          </div>
        </div>
      )}

      {/* CTA */}
      <button
        onClick={onNext}
        disabled={!canProceed || isSubmitting}
        className="w-full py-3 bg-pp-orange rounded-[10px] text-white font-semibold hover:bg-pp-orange/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {t('common.settingUp')}
          </>
        ) : (
          t('partnerStep.setupPregnancy')
        )}
      </button>
    </div>
  );
}
